// src/components/PanneauDetailControle.jsx — LE DÉTAIL D'UN CONTRÔLE, AU CLIC
// sur la pastille œil du Suivi (lot frise). LECTURE SEULE STRICTE : admin
// comme orchestrateur, on VOIT, on n'agit pas ici — les gestes (régler,
// cocher) vivent dans l'onglet Contrôle. AUCUN bouton d'action ; le seul
// bouton est « Fermer ».
//
// Données : action serveur `controle_detail` (admin+orchestrateur, cloisonnée
// serveur — photos en URLS SIGNÉES TTL 600 s, jamais un path brut).
// « contrôlé par [prénom] » ne s'affiche que si `controleur_prenom` est DANS
// la réponse — le serveur ne le sert qu'à l'admin, le client n'en décide pas.
//
// Vocabulaire de l'état = CELUI DU CONTRÔLEUR (ControleView.etatDeja) :
// « rien à signaler » / « N points · [prénom] reprend · depuis X » /
// « N points · repris ». Fermeture : Échap (hook pile partagé) + clic dehors.

import { useEffect, useState } from 'react'
import { CircleAlert, Eye, X } from 'lucide-react'
import { DS } from '../lib/ui'
import useEchapPourFermer from '../lib/useEchapPourFermer'
import { fetchControleDetail } from '../lib/controle-detail-api'
import { delaiReprise } from '../views/menage/ControleBilan'
import { CATEGORIES_POINTS, humaniserId, depuisTexte } from '../views/menage/ControleView'

// Même paires que la pastille : vert « clos », terracotta « ça attend ».
const VERT_FOND = '#f0f7f3'
const VERT_TEXTE = '#2c7a5c'
const TERRA_FOND = '#fdf4f0'
const TERRA_TEXTE = '#8f2f1d'

/** Le libellé d'un id de catégorie — la liste du contrôleur, repli humanisé. */
export function libelleCategorie(id) {
  const cat = CATEGORIES_POINTS.find((c) => c.id === id)
  return cat ? cat.label : humaniserId(id)
}

/**
 * L'ÉTAT en tête — même vocabulaire que la liste du contrôleur (etatDeja de
 * ControleView), adapté au Suivi : on n'est pas le contrôleur, donc jamais
 * « je reprends » — « [prénom] reprend » (admin, prénom servi) ou « le
 * contrôleur reprend » (orchestrateur, anonymat structurel préservé).
 */
export function phraseEtatControle(d) {
  const statut = d?.statut || null
  const n = Array.isArray(d?.points) ? d.points.length : 0
  const pts = `${n} point${n > 1 ? 's' : ''}`
  if (statut === 'bon') return { clos: true, texte: 'rien à signaler' }
  if (statut === 'regle') return { clos: true, texte: `${pts} · repris` }
  if (statut === 'reprise_en_cours' || statut === 'a_reprendre') {
    const depuis = depuisTexte(d?.cree_le)
    const qui = d?.reprise_par === 'controleur'
      ? (d?.controleur_prenom || 'le contrôleur')
      : (d?.agent_prenom || "l'agent")
    return { clos: false, texte: `${pts} · ${qui} reprend${depuis ? ` · ${depuis}` : ''}` }
  }
  return { clos: false, texte: pts }
}

export default function PanneauDetailControle({ controleId, onFermer }) {
  useEchapPourFermer(onFermer)
  const [detail, setDetail] = useState(null)
  const [erreur, setErreur] = useState(null)

  useEffect(() => {
    let annule = false
    setDetail(null)
    setErreur(null)
    fetchControleDetail(controleId)
      .then((d) => { if (!annule) setDetail(d) })
      .catch((e) => { if (!annule) setErreur(e?.message || 'Détail indisponible — réessayez.') })
    return () => { annule = true }
  }, [controleId])

  const etat = detail ? phraseEtatControle(detail) : null
  const faitParIndex = {}
  const photoRepriseParIndex = {}
  for (const r of detail?.reprise || []) {
    if (Number.isInteger(r?.index)) {
      faitParIndex[r.index] = !!r.fait
      if (r.photo_url) photoRepriseParIndex[r.index] = r.photo_url
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Détail du contrôle"
      data-testid="panneau-detail-controle"
      onClick={onFermer}
      style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(28,40,51,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 100%)', maxHeight: '86vh', overflowY: 'auto',
          background: '#fff', borderRadius: 14, padding: '16px 18px',
          fontFamily: DS.fontBody, color: '#2e3d4d',
          boxShadow: '0 10px 34px rgba(0,0,0,.24)' }}>

        {/* En-tête : l'œil du contrôle + fermer (SEUL bouton du panneau). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Eye size={16} strokeWidth={2.2} color="#3d5166" style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 600,
            color: '#2e3d4d', flex: 1, minWidth: 0 }}>
            Contrôle du ménage
          </span>
          <button type="button" onClick={onFermer} aria-label="Fermer"
            data-testid="panneau-detail-controle-fermer"
            style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', background: 'none', border: 'none',
              cursor: 'pointer', padding: 0, flexShrink: 0 }}>
            <X size={16} strokeWidth={2.2} color="#98a2ad" />
          </button>
        </div>

        {erreur && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
            background: DS.dangerBg, color: DS.dangerText, borderRadius: 8,
            fontSize: 12.5, lineHeight: 1.5 }}>
            <CircleAlert size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            {erreur}
          </div>
        )}
        {!erreur && !detail && (
          <div style={{ padding: '14px 0', fontSize: 12.5, color: DS.textMuted }}>Chargement…</div>
        )}

        {detail && (
          <>
            {/* L'ÉTAT en tête — vert clos, terracotta en attente. */}
            <div data-testid="panneau-detail-controle-etat"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 11px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                background: etat.clos ? VERT_FOND : TERRA_FOND,
                color: etat.clos ? VERT_TEXTE : TERRA_TEXTE, marginBottom: 10 }}>
              {etat.texte}
            </div>

            {/* ADMIN SEULEMENT (le serveur ne sert le prénom qu'à lui) : qui a
                contrôlé. L'orchestrateur ne reçoit jamais ce champ — la ligne
                n'existe pas pour lui. */}
            {(detail.controleur_prenom || detail.repris_le) && (
              <div data-testid="panneau-detail-controle-controleur"
                style={{ fontSize: 11.5, color: DS.textSecondary, marginBottom: 8 }}>
                {[
                  detail.controleur_prenom ? `contrôlé par ${detail.controleur_prenom}` : null,
                  // LE DÉLAI (maquette 2026-08-21) : signalé → repris.
                  detail.repris_le && delaiReprise(detail.cree_le, detail.repris_le)
                    ? `repris en ${delaiReprise(detail.cree_le, detail.repris_le)}` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            )}

            {/* LES POINTS — catégorie humanisée, texte, photos signées en
                vignettes, l'état de reprise point par point. Rien pour un
                contrôle « bon » : rien à lister. */}
            {(detail.points || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(detail.points || []).map((pt, i) => (
                  <div key={i} data-testid={`panneau-detail-controle-point-${i}`}
                    style={{ border: '1px solid #d5e3ee', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#3d5166', flex: 1 }}>
                        {libelleCategorie(pt.categorie)}
                      </span>
                      <span data-testid={`panneau-detail-controle-reprise-${i}`}
                        style={{ fontSize: 10.5, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 7,
                          background: faitParIndex[i] ? VERT_FOND : TERRA_FOND,
                          color: faitParIndex[i] ? VERT_TEXTE : TERRA_TEXTE }}>
                        {faitParIndex[i] ? 'repris' : 'à reprendre'}
                      </span>
                    </div>
                    {pt.texte && (
                      <div style={{ fontSize: 12, color: '#2e3d4d', lineHeight: 1.5 }}>{pt.texte}</div>
                    )}
                    {/* PHOTOS CÔTE À CÔTE (maquette 2026-08-21) : quand la
                        photo de REPRISE de l'agent existe, le CONSTAT et la
                        REPRISE se comparent d'un regard — celui qui a signalé
                        peut enfin VÉRIFIER. Sans photo de reprise : les
                        vignettes du constat, comme avant. */}
                    {photoRepriseParIndex[i] ? (
                      <div data-testid={`panneau-detail-controle-comparaison-${i}`}
                        style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginTop: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                            color: '#8f2f1d', textTransform: 'uppercase', marginBottom: 3 }}>
                            Le constat
                          </div>
                          <a href={(pt.photos || [])[0]} target="_blank" rel="noreferrer">
                            <img src={(pt.photos || [])[0]} alt={`Constat — ${libelleCategorie(pt.categorie)}`}
                              style={{ width: '100%', height: 76, objectFit: 'cover',
                                borderRadius: 8, border: '1.5px solid #e0b5a5', display: 'block' }} />
                          </a>
                        </div>
                        <span aria-hidden="true" style={{ alignSelf: 'center', color: '#98a2ad',
                          fontSize: 15, flexShrink: 0 }}>→</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                            color: '#1f5a43', textTransform: 'uppercase', marginBottom: 3 }}>
                            La reprise
                          </div>
                          <a href={photoRepriseParIndex[i]} target="_blank" rel="noreferrer">
                            <img src={photoRepriseParIndex[i]} alt={`Reprise — ${libelleCategorie(pt.categorie)}`}
                              style={{ width: '100%', height: 76, objectFit: 'cover',
                                borderRadius: 8, border: '1.5px solid #c9e0d4', display: 'block' }} />
                          </a>
                        </div>
                      </div>
                    ) : (pt.photos || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                        {(pt.photos || []).map((url, j) => (
                          <a key={j} href={url} target="_blank" rel="noreferrer">
                            <img src={url} alt={`Photo du point ${libelleCategorie(pt.categorie)}`}
                              style={{ width: 56, height: 56, objectFit: 'cover',
                                borderRadius: 7, border: '1px solid #d5e3ee', display: 'block' }} />
                          </a>
                        ))}
                      </div>
                    )}
                    {/* Les photos de constat SUPPLÉMENTAIRES (2e, 3e) restent
                        visibles sous la comparaison. */}
                    {photoRepriseParIndex[i] && (pt.photos || []).length > 1 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        {(pt.photos || []).slice(1).map((url, j) => (
                          <a key={j} href={url} target="_blank" rel="noreferrer">
                            <img src={url} alt={`Photo du constat ${j + 2}`}
                              style={{ width: 44, height: 44, objectFit: 'cover',
                                borderRadius: 7, border: '1px solid #d5e3ee', display: 'block' }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
